import { useT } from '@planetra/i18n';
import { Logo, Separator } from '@planetra/ui';
import { Outlet, useMatches, useRouteLoaderData } from 'react-router';
import type { RouteHandle } from '../app/routes';
import { RealtimeProvider } from '../realtime';
import { useSelectedOrganisation, type SessionData } from '../session';
import { AccountMenu } from './AccountMenu';
import { ApiErrorState } from './ApiErrorState';
import { ConnectionIndicator } from './ConnectionIndicator';
import { HeaderSlotsProvider, HeaderSlotTarget } from './HeaderSlots';
import { OfflineBanner } from './OfflineBanner';
import { PortalNav } from './PortalNav';

/**
 * The shell every route renders inside.
 *
 * The primary navigation rail sits between the header and the page
 * (P1-MVP-07); the workspace brings its own panels and runs dock. What is here is the part that has to exist from
 * the first commit because retrofitting it is how it gets skipped: a skip link,
 * a real `<main>` with an id for it to target, and a landmark structure a
 * screen reader can navigate.
 *
 * Everything rendered here is behind the session loader on the parent route
 * (P0-WEB-07), so it never paints without one. The header names the
 * organisation the session is acting for, because `X-Organisation-Id` decides
 * what every request returns and a user who cannot see which one is selected
 * has no way to explain why a project is missing. Switching it is in the
 * account menu (P0-WEB-08); the label stays here because the selected tenant
 * has to be legible without opening anything.
 *
 * `RealtimeProvider` lives here rather than in `App.tsx` because it calls
 * `useRevalidator` to satisfy the contract's reconnect rule, and that needs the
 * router — which `App.tsx` is outside of.
 */
export function AppLayout() {
  return (
    <RealtimeProvider>
      <Shell />
    </RealtimeProvider>
  );
}

function Shell() {
  const { t } = useT();
  const organisation = useSelectedOrganisation();
  // From the parent route rather than from `useLoaderData`, which would return
  // whichever child route is rendering.
  const session = useRouteLoaderData<SessionData>('session');
  const fullscreen = useMatches().some(
    (match) => (match.handle as RouteHandle | undefined)?.fullscreen === true,
  );

  // The session request failed for a reason that is not a 401 — a 500, or an
  // unreachable API. The user stays signed in and the shell says so, rather
  // than being bounced to sign-in over a problem that had nothing to do with
  // their session.
  const content =
    session?.failure === undefined ? (
      <Outlet />
    ) : (
      <div className="p-6">
        <ApiErrorState failure={session.failure} className="max-w-xl" />
      </div>
    );

  // A route that is the whole window (the Tools canvas) brings its own
  // chrome. The landmark and the offline banner stay: they are not chrome.
  if (fullscreen) {
    return (
      <div className="flex h-screen flex-col bg-canvas text-fg">
        <OfflineBanner />
        <main id="main" className="relative min-h-0 flex-1">
          {content}
        </main>
      </div>
    );
  }

  return (
    // `h-screen`, not `min-h-screen`. A child asking for `h-full` resolves
    // against a definite height; against a minimum it resolves to `auto`, and
    // the workspace collapses to its content in the top third of the window.
    // The scroll moves inside `main`, which is what an app shell wants anyway.
    <HeaderSlotsProvider>
      <div className="flex h-screen flex-col bg-canvas text-fg">
        {/* Visible only on focus. First tab stop on the page, so a keyboard user
          does not traverse the header on every navigation. */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-(--pl-z-toast) focus:rounded-md focus:bg-surface-raised focus:px-3 focus:py-2 focus:text-sm"
        >
          {t('common:skipToContent')}
        </a>

        <header className="flex h-(--pl-header-height) shrink-0 items-center justify-between border-b border-line px-4">
          <div className="flex min-w-0 items-center gap-3">
            <Logo label={t('common:appName')} className="h-5" />
            {organisation === undefined ? null : (
              <>
                <Separator orientation="vertical" className="h-4" />
                {/* Truncated rather than wrapped: the header has a fixed height,
                  and a long council name must not change it. */}
                <span className="truncate text-sm text-fg-muted" title={organisation.name}>
                  {organisation.name}
                </span>
              </>
            )}
            <HeaderSlotTarget slot="start" />
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <HeaderSlotTarget slot="end" />
            <ConnectionIndicator />
            {/* The language picker moved inside the account menu, where `AUTH-8`
              puts it. A second copy in the header would be two controls over
              one preference, and the header is the scarcer space. */}
            <AccountMenu />
          </div>
        </header>

        <OfflineBanner />

        <div className="flex min-h-0 flex-1">
          <PortalNav />

          <main id="main" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
            {content}
          </main>
        </div>
      </div>
    </HeaderSlotsProvider>
  );
}
